import * as React from 'react';
import { Loader2, X, User } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useUserSearch } from '@/api/users';
import type { ScoutAroundUser, OwnerInfo } from '@/types/api';

export interface UserAutocompleteProps {
  /** Currently selected user */
  value: OwnerInfo | null;
  /** Called when a user is selected or cleared */
  onChange: (user: OwnerInfo | null) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Test ID for QA automation */
  'data-testid'?: string;
}

/**
 * Formats a ScoutAround user for display.
 * Shows "First Last (email)" or just "email" if no name.
 */
function formatUserDisplay(user: ScoutAroundUser | OwnerInfo): string {
  if ('firstname' in user) {
    // ScoutAroundUser
    const nameParts = [user.firstname, user.lastname].filter(Boolean);
    const name = nameParts.length > 0 ? nameParts.join(' ') : null;
    return name ? `${name} (${user.email})` : user.email;
  } else {
    // OwnerInfo
    return user.name !== user.email ? `${user.name} (${user.email})` : user.email;
  }
}

/**
 * Convert ScoutAroundUser to OwnerInfo format
 */
function toOwnerInfo(user: ScoutAroundUser): OwnerInfo {
  const nameParts = [user.firstname, user.lastname].filter(Boolean);
  const name = nameParts.length > 0 ? nameParts.join(' ') : user.email;
  return {
    userId: user.id,
    email: user.email,
    name,
  };
}

/**
 * User autocomplete component for selecting ScoutAround users.
 * Provides debounced search with minimum 3 character requirement.
 */
export function UserAutocomplete({
  value,
  onChange,
  placeholder = 'Search by name or email...',
  required = false,
  disabled = false,
  error,
  'data-testid': testId,
}: UserAutocompleteProps) {
  const [inputValue, setInputValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => { clearTimeout(timer); };
  }, [inputValue]);

  // Fetch users based on debounced search
  const { data: searchResults, isLoading } = useUserSearch(debouncedSearch, {
    enabled: debouncedSearch.length >= 3 && isOpen,
  });

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  // Handle user selection
  const handleSelectUser = (user: ScoutAroundUser) => {
    onChange(toOwnerInfo(user));
    setInputValue('');
    setIsOpen(false);
  };

  // Handle clear
  const handleClear = () => {
    onChange(null);
    setInputValue('');
    inputRef.current?.focus();
  };

  const users = searchResults?.users ?? [];
  const showDropdown = isOpen && (users.length > 0 || (inputValue.length >= 3 && !isLoading));

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Selected user display or input */}
      {value ? (
        <div
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
            disabled ? 'cursor-not-allowed opacity-50' : 'border-input',
            error && 'border-destructive'
          )}
        >
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="truncate">{formatUserDisplay(value)}</span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="ml-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid={testId ? `${testId}-clear-btn` : undefined}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear</span>
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => { setIsOpen(true); }}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && !value && (
        <div
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {users.length > 0 ? (
            users.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => { handleSelectUser(user); }}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                data-testid={testId ? `${testId}-option-${String(user.id)}` : undefined}
              >
                <User className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="w-full min-w-0 overflow-hidden">
                  <span className="block truncate">{formatUserDisplay(user)}</span>
                </div>
              </button>
            ))
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              {inputValue.length < 3
                ? 'Type at least 3 characters to search'
                : 'No users found'}
            </div>
          )}
        </div>
      )}

      {/* Helper text for minimum characters */}
      {!value && inputValue.length > 0 && inputValue.length < 3 && isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-2 text-sm text-muted-foreground shadow-md">
          Type at least 3 characters to search
        </div>
      )}

      {/* Error message */}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}
